import { useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Collapse,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { PageHeader } from "../../components/cards/PageHeader";
import AppLoadingOverlay from "../../components/loaders/AppLoadingOverlay";
import BookingFormModal from "../../components/modals/BookingFormModal";
import BookingDetailsModal from "../../components/modals/BookingDetailsModal";
import { useCalendarScreen } from "./hooks/useCalendarScreen";
import MobileMultiCalendarView from "../../components/calendar/MobileMultiCalendarView";
import MobileSingleCalendarView from "../../components/calendar/MobileSingleCalendarView";
import CalendarLegend from "../../components/calendar/CalendarLegend";
import CalendarFiltersCard from "../../components/calendar/CalendarFiltersCard";
import DesktopTimelineCalendar from "../../components/calendar/DesktopTimelineCalendar";
import CalendarAvailablePeriodsCard from "../../components/calendar/CalendarAvailablePeriodsCard";
import { useAppSelector } from "../../store/hooks";

const CalendarScreen = () => {
  const vm = useCalendarScreen();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);
  const { bookingColors } = useAppSelector((state) => state.settings);

  const calendarModeActions = (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        width: { xs: "100%", md: "auto" },
        flexWrap: "wrap",
        justifyContent: { xs: "flex-start", md: "flex-end" },
        rowGap: 1,
      }}
    >
      <Button
        variant="outlined"
        startIcon={<RefreshIcon />}
        onClick={vm.refreshBookings}
        sx={{ borderRadius: "999px", fontWeight: 900 }}
      >
        {vm.t("common.refresh")}
      </Button>

      <ButtonGroup variant="outlined">
        <Button
          variant={vm.calendarMode === "multi" ? "contained" : "outlined"}
          onClick={() => vm.setCalendarMode("multi")}
          sx={{ fontWeight: 900 }}
        >
          {vm.t("calendar.multi")}
        </Button>

        <Button
          variant={vm.calendarMode === "single" ? "contained" : "outlined"}
          onClick={() => vm.setCalendarMode("single")}
          sx={{ fontWeight: 900 }}
        >
          {vm.t("calendar.single")}
        </Button>
      </ButtonGroup>
    </Stack>
  );

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 80px)",
          pb: 9,
        }}
      >
        <AppLoadingOverlay visible={vm.loading} />

        <PageHeader
          title={vm.t("tabs.calendar")}
          description={vm.t("calendar.subtitle")}
          action={calendarModeActions}
        />

        {vm.calendarMode === "multi" ? (
          <>
            <CalendarLegend units={vm.units} t={vm.t} />

            <Collapse in={mobileFiltersOpen}>
              <CalendarFiltersCard
                t={vm.t}
                startDate={vm.startDate}
                endDate={vm.endDate}
                setStartDate={vm.setStartDate}
                setEndDate={vm.setEndDate}
                applyFilters={vm.applyFilters}
                setThisWeek={vm.setThisWeek}
                setNext7Days={vm.setNext7Days}
                setNext14Days={vm.setNext14Days}
                setThisMonth={vm.setThisMonth}
              />
            </Collapse>

            <MobileMultiCalendarView
              t={vm.t}
              dates={vm.dates}
              properties={vm.properties}
              units={vm.units}
              apiStartDate={vm.apiStartDate}
              apiEndDate={vm.apiEndDate}
              openPropertyIds={vm.openPropertyIds}
              pendingBookingSelection={vm.pendingBookingSelection}
              bookingColors={bookingColors}
              canManage={vm.canManage}
              filtersOpen={mobileFiltersOpen}
              getUnitsForProperty={vm.getUnitsForProperty}
              getBookingsForUnit={vm.getBookingsForUnit}
              toggleProperty={vm.toggleProperty}
              handleCalendarDateClick={vm.handleCalendarDateClick}
              openCreateBookingModal={vm.openCreateBookingModal}
              openBookingDetails={vm.openBookingDetails}
              previousRange={vm.previousRange}
              nextRange={vm.nextRange}
              refreshBookings={vm.refreshBookings}
              toggleFilters={() => setMobileFiltersOpen((open) => !open)}
            />

            <CalendarAvailablePeriodsCard
              t={vm.t}
              units={vm.units}
              bookings={vm.bookings}
              from={vm.apiStartDate}
              until={vm.apiEndDate}
            />
          </>
        ) : (
          <Card>
            <CardContent>
              <MobileSingleCalendarView
                t={vm.t}
                firstMonthDate={vm.singleMonthDate}
                selectedUnit={vm.selectedSingleUnit}
                bookings={vm.singleUnitBookings}
                selectedStartDate={vm.selectedStartDate}
                setSingleMonthDate={vm.setSingleMonthDate}
                previousSingleUnit={vm.previousSingleUnit}
                nextSingleUnit={vm.nextSingleUnit}
                previousMonth={vm.previousMonth}
                nextMonth={vm.nextMonth}
                onDatePress={vm.handleSingleCalendarDatePress}
                onBookingPress={vm.openBookingDetails}
              />
            </CardContent>
          </Card>
        )}

        <BookingFormModal
          open={vm.bookingFormOpen}
          loading={vm.loading}
          units={vm.units}
          booking={vm.editingBooking}
          defaultUnitId={
            vm.selectedUnit?.id || vm.selectedBooking?.unit_id || null
          }
          defaultCheckIn={vm.selectedStartDate}
          defaultCheckOut={vm.selectedEndDate}
          defaultCurrency={vm.defaultCurrency}
          onClose={vm.closeBookingForm}
          onSubmit={vm.submitBooking}
        />

        <BookingDetailsModal
          open={vm.bookingDetailsOpen}
          booking={vm.selectedBooking}
          loading={vm.loading}
          canManage={vm.canManage}
          onClose={vm.closeBookingDetails}
          onEdit={vm.openEditBooking}
          onCancelBooking={vm.confirmCancelBooking}
          onPaymentStatusChange={vm.quickUpdatePaymentStatus}
          onSendInvoice={vm.confirmSendInvoice}
        />
      </Box>
    );
  }

  return (
    <Box>
      <AppLoadingOverlay visible={vm.loading} />

      <PageHeader
        title={vm.t("tabs.calendar")}
        description={vm.t("calendar.subtitle")}
        action={calendarModeActions}
      />

      {vm.calendarMode === "multi" ? (
        <>
          <CalendarLegend units={vm.units} t={vm.t} />

          <Collapse in={desktopFiltersOpen}>
            <CalendarFiltersCard
              t={vm.t}
              startDate={vm.startDate}
              endDate={vm.endDate}
              setStartDate={vm.setStartDate}
              setEndDate={vm.setEndDate}
              applyFilters={vm.applyFilters}
              setThisWeek={vm.setThisWeek}
              setNext7Days={vm.setNext7Days}
              setNext14Days={vm.setNext14Days}
              setThisMonth={vm.setThisMonth}
            />
          </Collapse>

          <DesktopTimelineCalendar
            t={vm.t}
            dates={vm.dates}
            properties={vm.properties}
            units={vm.units}
            apiStartDate={vm.apiStartDate}
            apiEndDate={vm.apiEndDate}
            openPropertyIds={vm.openPropertyIds}
            pendingBookingSelection={vm.pendingBookingSelection}
            bookingColors={bookingColors}
            canManage={vm.canManage}
            filtersOpen={desktopFiltersOpen}
            getUnitsForProperty={vm.getUnitsForProperty}
            getBookingsForUnit={vm.getBookingsForUnit}
            toggleProperty={vm.toggleProperty}
            handleCalendarDateClick={vm.handleCalendarDateClick}
            openCreateBookingModal={vm.openCreateBookingModal}
            openBookingDetails={vm.openBookingDetails}
            previousRange={vm.previousRange}
            nextRange={vm.nextRange}
            goToToday={vm.goToToday}
            setFullYear={vm.setFullYear}
            refreshBookings={vm.refreshBookings}
            toggleFilters={() => setDesktopFiltersOpen((open) => !open)}
          />

          <CalendarAvailablePeriodsCard
            t={vm.t}
            units={vm.units}
            bookings={vm.bookings}
            from={vm.apiStartDate}
            until={vm.apiEndDate}
          />
        </>
      ) : (
        <Card>
          <CardContent>
            <MobileSingleCalendarView
              t={vm.t}
              firstMonthDate={vm.singleMonthDate}
              selectedUnit={vm.selectedSingleUnit}
              bookings={vm.singleUnitBookings}
              selectedStartDate={vm.selectedStartDate}
              setSingleMonthDate={vm.setSingleMonthDate}
              previousSingleUnit={vm.previousSingleUnit}
              nextSingleUnit={vm.nextSingleUnit}
              previousMonth={vm.previousMonth}
              nextMonth={vm.nextMonth}
              onDatePress={vm.handleSingleCalendarDatePress}
              onBookingPress={vm.openBookingDetails}
            />
          </CardContent>
        </Card>
      )}

      <BookingFormModal
        open={vm.bookingFormOpen}
        loading={vm.loading}
        units={vm.units}
        booking={vm.editingBooking}
        defaultUnitId={
          vm.selectedUnit?.id || vm.selectedBooking?.unit_id || null
        }
        defaultCheckIn={vm.selectedStartDate}
        defaultCheckOut={vm.selectedEndDate}
        defaultCurrency={vm.defaultCurrency}
        onClose={vm.closeBookingForm}
        onSubmit={vm.submitBooking}
      />

      <BookingDetailsModal
        open={vm.bookingDetailsOpen}
        booking={vm.selectedBooking}
        loading={vm.loading}
        canManage={vm.canManage}
        onClose={vm.closeBookingDetails}
        onEdit={vm.openEditBooking}
        onCancelBooking={vm.confirmCancelBooking}
        onPaymentStatusChange={vm.quickUpdatePaymentStatus}
        onSendInvoice={vm.confirmSendInvoice}
      />
    </Box>
  );
};

export default CalendarScreen;
